import moment from 'moment'

// All search result types
export enum SearchResultGroupName {
	PAGE = 'page',
	CREATOR = 'creator',
	ACCOUNT = 'account',
	COLLAB = 'collab',
	USER = 'user',
	PROJECT = 'project',
	AGREEMENT = 'agreement'
}

const DEFAULT_SORT_FUNCTION = (a, b) => {
	if (a.score !== b.score) return b.score - a.score
	if (a.viewedAt !== b.viewedAt) return (b.viewedAt || 0) - (a.viewedAt || 0)
	if (a.updatedAt !== b.updatedAt) return (b.updatedAt || 0) - (a.updatedAt || 0)
	return a.title.localeCompare(b.title)
}

// Scoring constants
const SCORES = {
	EXACT_MATCH: 1000000,
	TITLE_PREFIX: 1000,
	KEYWORD_MATCH: 10,
	SINGLE_KEYWORD: 50,
	PARTIAL_KEYWORD: 10,
	RECENT_CREATOR: 10,
	RECENT_UPDATE: 10
}

export interface SpotlightResult {
	id: string
	type: SearchResultGroupName
	title: string
	description?: string
	imageUrl?: string
	icon?: string
	meta?: string
	actionIcons?: string[]
	actionLabel?: string
	updatedAt?: number
	viewedAt?: number
	actionKeywords?: string[]
	exactMatchTerms?: string[]
	route?: {
		name?: string
		path?: string
		params?: Record<string, any>
		query?: Record<string, any>
	}
	onSelect?: () => void
}

export interface ScoredSpotlightResult extends SpotlightResult {
	score: number
}

export interface GroupedResults extends Record<SearchResultGroupName, ScoredSpotlightResult[]> {}

// Configuration for each search result type
interface SearchResultGroupConfig {
	type: SearchResultGroupName
	title: string
	icon: string
	groupPriority: number
	keywords: string[]
	source: (rootGetters: any, rootState: any) => any[]
	buildResult: (getters: any, rootGetters: any) => (item: any) => SpotlightResult
	onSelect?: (item: any) => void
}

// Central configuration for all search result types
const searchSourceConfigs: Record<SearchResultGroupName, SearchResultGroupConfig> = {
	[SearchResultGroupName.CREATOR]: {
		type: SearchResultGroupName.CREATOR,
		title: 'Creators',
		icon: 'user',
		groupPriority: 1,
		keywords: ['find', 'search', 'creator', 'influencer', 'person', 'contact', 'people'],
		source: rootGetters => rootGetters['account/creators'] || [],
		buildResult: (getters, rootGetters) => creator => {
			// Get creator
			if (!creator) return

			// Get social entities for this creator
			const socialEntities = rootGetters['socialEntity/accounts'][creator._id] || []

			// Extract social entity terms for exact matching
			const socialEntityExactTerms = socialEntities
				.map(entity => [entity.name, entity.username, entity.url, entity.visionId])
				.flat()
				.filter(Boolean)

			const platforms = [
				...new Set(socialEntities.map(entity => entity.platform))
			] as string[]
			// make sure they're SORTED in the order youtube > instagram > tiktok
			platforms.sort((a, b) => {
				const order = ['tiktok', 'instagram', 'youtube']
				return order.indexOf(a) - order.indexOf(b)
			})

			const result: SpotlightResult = {
				id: creator._id,
				type: SearchResultGroupName.CREATOR,
				title: creator.name ?? creator.legalName ?? 'Unnamed Creator',
				actionIcons: platforms,
				imageUrl: creator.logo,
				icon: 'user',
				meta: creator.viewedAt ? `Viewed ${moment(creator.viewedAt).fromNow()}` : '',
				viewedAt: creator.viewedAt ? new Date(creator.viewedAt).getTime() : null,
				exactMatchTerms: [
					creator.name,
					creator.legalName,
					creator.youtube,
					creator.instagram,
					creator.tiktok,
					creator.email,
					...socialEntityExactTerms
				].filter(Boolean),
				route: { name: 'creator', params: { creatorId: creator._id } }
			}
			return result
		}
	},

	[SearchResultGroupName.ACCOUNT]: {
		type: SearchResultGroupName.ACCOUNT,
		title: 'Switch Accounts',
		icon: 'building',
		groupPriority: 2,
		keywords: [
			'switch',
			'account',
			'accounts',
			'change',
			'company',
			'companies',
			'client',
			'clients'
		],
		source: rootGetters =>
			rootGetters['account/forUser'].filter(a => a.isTestAccount !== true) || [],
		buildResult: (getters, rootGetters) => brand => {
			const result: SpotlightResult = {
				id: brand._id,
				type: SearchResultGroupName.ACCOUNT,
				title: brand.name ?? 'Unnamed Account',
				description: brand.description,
				imageUrl: brand.logo,
				actionIcons: ['refresh'],
				actionLabel: 'Switch Account',
				icon: 'building',
				meta: brand.updatedAt ? `Updated ${moment(brand.updatedAt).fromNow()}` : '',
				exactMatchTerms: [brand.name].filter(Boolean),
				onSelect: () => {
					return {
						type: 'account-switch',
						accountId: brand._id
					}
				}
			}
			return result
		}
	},

	[SearchResultGroupName.COLLAB]: {
		type: SearchResultGroupName.COLLAB,
		title: 'Collaborations',
		icon: 'users',
		groupPriority: 3,
		keywords: ['collab', 'collaboration', 'project', 'campaign', 'partnership'],
		source: rootGetters => rootGetters['collab/forSelectedBrand'] || [],
		buildResult: (getters, rootGetters) => collab => {
			const project = rootGetters['project/byId'](collab.project) || {}
			const creator = rootGetters['account/byId'](collab.creator) || {}

			const result: SpotlightResult = {
				id: collab._id,
				type: SearchResultGroupName.COLLAB,
				title: creator.name ?? 'Unknown Creator',
				description: project.name ?? 'Unknown Project',
				icon: 'users',
				imageUrl: creator.logo,
				meta: collab.updatedAt ? `Updated ${moment(collab.updatedAt).fromNow()}` : '',
				updatedAt: new Date(collab.updatedAt).getTime(),
				exactMatchTerms: [project.name, creator.name].filter(Boolean),
				route: { name: 'collab', params: { collabId: collab._id } }
			}
			return result
		}
	},

	[SearchResultGroupName.AGREEMENT]: {
		type: SearchResultGroupName.AGREEMENT,
		title: 'Agreements',
		icon: 'file',
		groupPriority: 4,
		keywords: ['agreements', 'contracts', 'w9', 'contract', 'agreement', 'sign'],
		source: rootGetters =>
			rootGetters['agreement/forSelectedAccount']
				.map(agreement => {
					const project = rootGetters['project/byId'](agreement.project) || {}
					const creator = rootGetters['account/byId'](agreement.creator) || {}
					const collab = rootGetters['collab/byId'](agreement.collab) || {}

					if (
						!project._id ||
						!creator._id ||
						!collab._id ||
						project.deleted ||
						collab.deleted ||
						creator.deleted
					) {
						return false
					}

					return {
						...agreement,
						project: rootGetters['project/byId'](agreement.project),
						creator: rootGetters['account/byId'](agreement.creator),
						collab: rootGetters['collab/byId'](agreement.collab)
					}
				})
				.filter(Boolean) || [],
		buildResult: (getters, rootGetters) => agreement => {
			const result: SpotlightResult = {
				id: agreement._id,
				type: SearchResultGroupName.AGREEMENT,
				title: agreement.creator.name ?? agreement.creator.legalName ?? 'Unknown Creator',
				description: agreement.project.name ?? 'Unknown Project',
				icon: 'file',
				// imageUrl: project.image || creator.logo,
				meta: agreement.status || `Updated ${moment(agreement.updatedAt).fromNow()}` || '',
				updatedAt: new Date(agreement.updatedAt).getTime(),
				exactMatchTerms: [agreement.project.name, agreement.creator.name].filter(Boolean),
				route: {
					name: 'collab',
					params: {
						collabId: agreement.collab.id,
						section: 'agreement'
					}
				}
			}
			return result
		}
	},

	[SearchResultGroupName.PROJECT]: {
		type: SearchResultGroupName.PROJECT,
		title: 'Projects',
		icon: 'favorites',
		groupPriority: 4,
		keywords: ['project', 'campaign', 'performance', 'metric', 'chart', 'graph', 'stat'],
		source: rootGetters => rootGetters['project/forSelectedAccount'] || [],
		buildResult: (getters, rootGetters) => project => {
			const result: SpotlightResult = {
				id: project._id,
				type: SearchResultGroupName.PROJECT,
				title: project.name ?? 'Project',
				description: project.description || '',
				icon: 'favorites',
				imageUrl: project.image || '',
				meta: project.updatedAt ? `Updated ${moment(project.updatedAt).fromNow()}` : '',
				updatedAt: new Date(project.updatedAt).getTime(),
				exactMatchTerms: [project.name, project.title, project.description].filter(Boolean),
				route: { name: 'project', params: { projectId: project._id } }
			}
			return result
		}
	},

	[SearchResultGroupName.USER]: {
		type: SearchResultGroupName.USER,
		title: 'Team Members',
		icon: 'user',
		groupPriority: 5,
		keywords: ['team', 'user', 'member', 'person', 'contact', 'staff', 'members'],
		source: rootGetters => rootGetters['collaboration/forSelectedAccount'] || [],
		buildResult: (getters, rootGetters) => user => {
			const result: SpotlightResult = {
				id: user._id,
				type: SearchResultGroupName.USER,
				title: user.name ?? user.email ?? 'Unnamed Team Member',
				description: user.role || 'Team Member',
				icon: 'user',
				meta: user.updatedAt ? `Updated ${moment(user.updatedAt).fromNow()}` : '',
				exactMatchTerms: [user.name, user.email].filter(Boolean),
				route: { name: 'team', query: { user: user._id } }
			}
			return result
		}
	},

	[SearchResultGroupName.PAGE]: {
		type: SearchResultGroupName.PAGE,
		title: 'Go to a page',
		icon: 'link',
		groupPriority: 6,
		keywords: ['go', 'to', 'navigate', 'page', 'visit', 'open', 'link', 'nav', 'menu'],
		source: () => [
			{
				name: 'home',
				meta: {
					search: {
						enabled: true,
						title: 'Home Dashboard',
						description: 'Your main dashboard and overview',
						keywords:
							'home dashboard overview main start beginning welcome summary analytics metrics'
					}
				}
			},
			{
				name: 'projects',
				meta: {
					search: {
						enabled: true,
						title: 'Projects',
						description: 'View and manage all your projects',
						keywords:
							'projects project management campaigns work tasks portfolio initiatives'
					}
				}
			},
			{
				name: 'team',
				meta: {
					search: {
						enabled: true,
						title: 'Team',
						description: 'Manage team members and permissions',
						keywords:
							'team members users collaboration permissions staff employees roles access management'
					}
				}
			},
			{
				name: 'billing',
				meta: {
					search: {
						enabled: true,
						title: 'Billing',
						description: 'Manage billing, invoices, and subscriptions',
						keywords:
							'billing invoices subscription payment credit card invoice charges cost price money financial accounting'
					}
				}
			},
			{
				name: 'payments',
				meta: {
					search: {
						enabled: true,
						title: 'Payments/Taxes',
						description: 'View payment history, taxes, and payouts',
						keywords:
							'payments taxes payouts history transactions money financial earnings revenue income tax forms 1099'
					}
				}
			},
			{
				name: 'user',
				meta: {
					search: {
						enabled: true,
						title: 'User Settings',
						description: 'Manage your profile and account settings',
						keywords:
							'settings profile user account personal preferences configuration options privacy notifications'
					}
				}
			}
		],
		buildResult: (getters, rootGetters) => route => ({
			id: route.name,
			type: SearchResultGroupName.PAGE,
			title: route.meta.search.title ?? route.name ?? 'Unnamed Page',
			description: route.meta.search.description,
			icon: 'link',
			exactMatchTerms: [
				route.meta.search.title,
				route.name,
				...(route.meta.search.keywords ? route.meta.search.keywords.split(' ') : [])
			].filter(Boolean),
			route: { name: route.name }
		})
	}
}

interface SearchIndex {
	id: string
	type: SpotlightResult['type']
	searchText: string
	result: SpotlightResult
}

interface SpotlightSearchState {
	isIndexBuilt: boolean
	indexes: SearchIndex[]
	lastIndexBuild: number
}

export default {
	namespaced: true,
	state: (): SpotlightSearchState => ({
		isIndexBuilt: false,
		indexes: [],
		lastIndexBuild: 0
	}),

	getters: {
		search:
			(state, getters, rootState, rootGetters) =>
			(query: string): ScoredSpotlightResult[] => {
				if (!state.isIndexBuilt) {
					return []
				}

				const trimmedQuery = query.toLowerCase().trim()

				if (trimmedQuery.length < 2) {
					return []
				}

				const queryWords = trimmedQuery.split(/\s+/)

				const matches = state.indexes
					.map(index => {
						const result = { ...index.result }

						const group = searchSourceConfigs[result.type] as SearchResultGroupConfig
						let score = 0

						// Check for exact match (highest priority)
						if (
							result.exactMatchTerms?.some(
								term => term.toLowerCase() === trimmedQuery
							)
						) {
							score += SCORES.EXACT_MATCH
						}

						// Check if search text contains the full query
						if (index.searchText.includes(trimmedQuery)) {
							// Title prefix match (very high priority)
							if (result.title.toLowerCase().startsWith(trimmedQuery)) {
								score += SCORES.TITLE_PREFIX
							}

							// Action keyword matches (exact and partial)
							let exactKeywordMatches = 0
							let partialKeywordMatches = 0
							queryWords.forEach(word => {
								if (result.actionKeywords?.includes(word)) {
									exactKeywordMatches++
								} else {
									// Check for partial matches
									const partialMatch = result.actionKeywords?.some(
										keyword => keyword.includes(word) || word.includes(keyword)
									)
									if (partialMatch) {
										partialKeywordMatches++
									}
								}
							})

							if (exactKeywordMatches > 1) {
								score += SCORES.KEYWORD_MATCH * exactKeywordMatches
							} else if (exactKeywordMatches === 1) {
								score += SCORES.SINGLE_KEYWORD
							}

							if (partialKeywordMatches > 0) {
								score += SCORES.PARTIAL_KEYWORD * partialKeywordMatches
							}

							// Base match score if no keywords found but text matches
							if (exactKeywordMatches === 0 && partialKeywordMatches === 0) {
								score += 1
							}
						} else {
							// Check for partial keyword matches even if full query doesn't match
							let keywordMatches = 0
							queryWords.forEach(word => {
								if (index.searchText.includes(word)) {
									keywordMatches++
								}
								if (result.actionKeywords?.includes(word)) {
									keywordMatches++
								}
								if (group.keywords.includes(word)) {
									keywordMatches++
								}
							})

							if (keywordMatches > 0) {
								score += SCORES.SINGLE_KEYWORD * keywordMatches
							}
						}

						// Existing bonuses
						if (result.type === 'creator' && result.viewedAt) {
							const recentlyViewedCreators =
								rootGetters['user/me']?.recentlyViewedCreators || []
							if (recentlyViewedCreators.includes(result.id)) {
								score += SCORES.RECENT_CREATOR
							}
						}

						if (
							result.updatedAt &&
							Date.now() - result.updatedAt < 7 * 24 * 60 * 60 * 1000
						) {
							score += SCORES.RECENT_UPDATE
						}

						return { ...result, score }
					})
					.filter(item => item.score > 0)

				return matches
			},

		groupResults:
			(state, getters, rootState, rootGetters) =>
			(results: ScoredSpotlightResult[]): GroupedResults => {
				if (!results.length) {
					return {} as GroupedResults
				}

				// Group results by type using config
				const grouped: Partial<GroupedResults> = {}

				// First, group items by type and track highest score per group
				const groupScores: Record<SearchResultGroupName, number> = {} as Record<
					SearchResultGroupName,
					number
				>

				Object.values(searchSourceConfigs).forEach(config => {
					const items = results
						.filter(result => result.type === config.type)
						.sort(DEFAULT_SORT_FUNCTION)

					if (items.length > 0) {
						grouped[config.type] = items
						// Track the highest score in this group
						groupScores[config.type] = Math.max(...items.map(item => item.score))
					}
				})

				// Sort groups by highest score within the group (descending), with priority as fallback
				const sortedGroupEntries = Object.entries(grouped).sort(([typeA], [typeB]) => {
					const configA = searchSourceConfigs[typeA as SearchResultGroupName]
					const configB = searchSourceConfigs[typeB as SearchResultGroupName]
					const scoreA = groupScores[typeA as SearchResultGroupName]
					const scoreB = groupScores[typeB as SearchResultGroupName]

					// If both scores are 0, fall back to priority ordering
					if (scoreA === 0 && scoreB === 0) {
						return configA.groupPriority - configB.groupPriority
					}

					// Otherwise sort by highest score (descending)
					return scoreB - scoreA
				})

				// Rebuild the grouped object in the correct order
				const orderedGrouped: Partial<GroupedResults> = {}
				sortedGroupEntries.forEach(([type, items]) => {
					orderedGrouped[type as SearchResultGroupName] = items
				})

				return orderedGrouped as GroupedResults
			},

		getGroupTitle:
			() =>
			(groupType: SearchResultGroupName): string => {
				const config = Object.values(searchSourceConfigs).find(c => c.type === groupType)
				return config?.title || groupType
			},

		flattenResults:
			() =>
			(
				groupedResults: Partial<GroupedResults>
			): {
				result: ScoredSpotlightResult
				groupType: SearchResultGroupName
				index: number
			}[] => {
				const flat: {
					result: ScoredSpotlightResult
					groupType: SearchResultGroupName
					index: number
				}[] = []

				Object.entries(groupedResults).forEach(([groupType, results]) => {
					if (results) {
						results.forEach((result, index) => {
							flat.push({
								result,
								groupType: groupType as SearchResultGroupName,
								index
							})
						})
					}
				})

				return flat
			},

		defaultResults: (state, getters, rootState, rootGetters) => (): Partial<GroupedResults> => {
			const results: Partial<GroupedResults> = {}

			// Recent creators - show most recently viewed first
			const recentlyViewedCreators = rootGetters['user/me']?.recentlyViewedCreators || []
			const uniqueRecentlyViewedCreators = [...new Set(recentlyViewedCreators)]
			const creators = searchSourceConfigs[SearchResultGroupName.CREATOR].source(
				rootGetters,
				rootState
			)
			const buildCreatorResult = searchSourceConfigs[
				SearchResultGroupName.CREATOR
			].buildResult(getters, rootGetters)

			// Get creators from recently viewed list (preserves order - most recent first)
			const recentCreators = uniqueRecentlyViewedCreators
				.map(id => creators.find(c => c._id === id))
				.map(buildCreatorResult)
				.filter(Boolean)
				.map(creator => ({ ...creator, score: 0 }))

			if (recentCreators.length > 0) {
				results[SearchResultGroupName.CREATOR] = recentCreators
			}

			// Recent projects - already correctly sorted by most recent first
			const projects = searchSourceConfigs[SearchResultGroupName.PROJECT].source(
				rootGetters,
				rootState
			)
			const buildProjectResult = searchSourceConfigs[
				SearchResultGroupName.PROJECT
			].buildResult(getters, rootGetters)
			const recentProjects = projects
				.map(buildProjectResult)
				.filter(Boolean)
				.map(project => ({ ...project, score: 0 }))
				.sort(DEFAULT_SORT_FUNCTION)

			if (recentProjects.length > 0) {
				results[SearchResultGroupName.PROJECT] = recentProjects
			}

			// Recent collabs - already correctly sorted by most recent first
			const collabs = searchSourceConfigs[SearchResultGroupName.COLLAB].source(
				rootGetters,
				rootState
			)
			const buildCollabResult = searchSourceConfigs[SearchResultGroupName.COLLAB].buildResult(
				getters,
				rootGetters
			)
			const recentCollabs = collabs
				.map(buildCollabResult)
				.filter(Boolean)
				.map(collab => ({ ...collab, score: 0 }))
				.sort(DEFAULT_SORT_FUNCTION)

			if (recentCollabs.length > 0) {
				results[SearchResultGroupName.COLLAB] = recentCollabs
			}

			return results
		}
	},

	mutations: {
		SET_INDEX_BUILT(state, value: boolean) {
			state.isIndexBuilt = value
		},

		SET_INDEXES(state, indexes: SearchIndex[]) {
			state.indexes = indexes
			state.lastIndexBuild = Date.now()
		}
	},

	actions: {
		buildIndex({ commit, getters, rootGetters, rootState }) {
			const indexes: SearchIndex[] = []

			// Build indexes for all configured search result types
			Object.values(searchSourceConfigs).forEach(config => {
				const items = config
					.source(rootGetters, rootState)
					.filter(item => item.deleted !== true)
				const buildResult = config.buildResult(getters, rootGetters)

				items.forEach(item => {
					const result = buildResult(item)
					if (result) {
						indexes.push({
							id: result.id,
							type: config.type,
							searchText: [...result.exactMatchTerms, ...config.keywords]
								.join('\n')
								.toLowerCase(),
							result
						})
					}
				})
			})

			console.debug('🔍 Final search indexes:', {
				totalIndexes: indexes.length,
				accountIndexes: indexes.filter(idx => idx.type === SearchResultGroupName.ACCOUNT)
					.length,
				accountResults: indexes
					.filter(idx => idx.type === SearchResultGroupName.ACCOUNT)
					.map(idx => idx.result)
			})

			commit('SET_INDEXES', indexes)
			commit('SET_INDEX_BUILT', true)
		},

		ensureIndexBuilt({ state, dispatch }) {
			if (!state.isIndexBuilt) {
				dispatch('buildIndex')
			}
		},

		rebuildIndex: debounce(({ dispatch }) => {
			dispatch('buildIndex')
		}, 300),

		// Watch for account switches and rebuild index
		subscribe({ dispatch }, store) {
			const watchers = []

			// Create a debounced rebuild function for data changes (10 seconds)
			const debouncedDataRebuild = debounce(() => {
				console.debug('🔄 Data changed, rebuilding search index')
				dispatch('buildIndex')
			}, 10000)

			// Watch for selected account changes (immediate rebuild)
			watchers.push(
				store.watch(
					state => state.account?.selectedId,
					(newAccountId, oldAccountId) => {
						if (newAccountId !== oldAccountId) {
							console.debug(
								'🔄 Account switched, rebuilding search index immediately'
							)
							dispatch('buildIndex')
						}
					}
				)
			)

			const sources = Object.values(searchSourceConfigs)
			sources.push({
				title: 'Social Entities',
				source: (getters, state) => getters['socialEntity/all']
			} as any)

			// Watch all search source configs automatically
			sources.forEach(config => {
				watchers.push(
					store.watch(
						(state, getters) => config.source(getters, state),
						(newValue, oldValue) => {
							// Only trigger rebuild if data actually changed meaningfully
							const hasChanged =
								(Array.isArray(newValue) &&
									Array.isArray(oldValue) &&
									newValue.length !== oldValue.length) ||
								(Array.isArray(newValue) && !oldValue && newValue.length > 0) ||
								(!Array.isArray(newValue) && newValue !== oldValue)

							if (hasChanged) {
								console.debug(
									`🔄 ${config.title} data changed, triggering debounced rebuild ${newValue && Array.isArray(newValue) && newValue.length}`
								)
								debouncedDataRebuild()
							}
						}
						// { deep: true } // skipping this so it's maybe more performant?
					)
				)
			})

			// Return cleanup function
			return () => {
				watchers.forEach(unwatch => unwatch())
			}
		},

		// Initialize the search store and set up watchers
		init({ dispatch }, store) {
			// Set up all the watchers
			dispatch('subscribe', store)
		}
	}
}

// Simple debounce utility
function debounce(func: (...args: any[]) => void, wait: number) {
	let timeout: NodeJS.Timeout
	return function executedFunction(...args: any[]) {
		const later = () => {
			clearTimeout(timeout)
			func(...args)
		}
		clearTimeout(timeout)
		timeout = setTimeout(later, wait)
	}
}
